import { Link, useNavigate } from "react-router-dom";
import { HandHeart, Moon, Sun, Globe, Menu, LogOut, LayoutDashboard } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useI18n, LANGUAGES } from "../i18n";
import { useTheme, useSettings } from "../lib/providers";
import { publicFileUrl } from "../lib/plans";
import { useAuth } from "../lib/auth";

const LINKS = [
  { to: "/", key: "nav_home", id: "home" },
  { to: "/#about", key: "vm_eyebrow", id: "about" },
  { to: "/#packages", key: "nav_packages", id: "packages" },
  { to: "/#testimonials", key: "nav_testimonials", id: "testimonials" },
  { to: "/help", key: "nav_help", id: "help" },
  { to: "/#contact", key: "nav_contact", id: "contact" },
];

const LangMenu = () => {
  const { lang, setLang } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors hover:bg-accent" data-testid="language-selector">
        <Globe className="h-4 w-4" style={{ color: "var(--gold)" }} /> {LANGUAGES.find((l) => l.code === lang)?.short}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`language-option-${l.code}`} className={lang === l.code ? "font-semibold" : ""}>
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  const { settings } = useSettings();
  if (settings && settings.theme.allow_toggle === false) return null;
  return (
    <button onClick={toggle} aria-label="Toggle theme" className="flex h-9 w-9 items-center justify-center rounded-full border transition-colors hover:bg-accent" data-testid="theme-toggle">
      {theme === "dark" ? <Sun className="h-4 w-4" style={{ color: "var(--gold)" }} /> : <Moon className="h-4 w-4" style={{ color: "var(--gold)" }} />}
    </button>
  );
};

const AuthButtons = ({ mobile }) => {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  if (user) {
    return (
      <div className={`flex ${mobile ? "flex-col" : "items-center"} gap-2`}>
        <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="btn-ghost-gold !px-4 !py-2" data-testid="nav-dashboard-link">
          <LayoutDashboard className="h-4 w-4" /> {user.role === "admin" ? t("nav_admin") : t("nav_dashboard")}
        </Link>
        <button onClick={() => { signOut(); nav("/"); }} className="flex items-center justify-center gap-1 rounded-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="nav-logout-btn">
          <LogOut className="h-4 w-4" /> {t("nav_logout")}
        </button>
      </div>
    );
  }
  return (
    <div className={`flex ${mobile ? "flex-col" : "items-center"} gap-2`}>
      <Link to="/login" className="rounded-full px-4 py-2 text-sm font-medium hover:text-[color:var(--gold)]" data-testid="nav-login-link">{t("nav_login")}</Link>
      <Link to="/register" className="btn-gold !px-5 !py-2" data-testid="nav-register-link">{t("nav_register")}</Link>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const b = settings?.branding || {};
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          {b.logo_path ? <img src={publicFileUrl(b.logo_path)} alt="" className="h-10 w-10 rounded-xl object-contain" data-testid="nav-logo-img" /> : (
          <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "var(--gold-soft)", border: "1px solid var(--gold)" }}>
            <HandHeart className="h-5 w-5" style={{ color: "var(--gold)" }} />
          </span>)}
          <span className="leading-tight">
            <span className="block font-serif text-xl font-bold gold-text" data-testid="nav-site-name">{b.site_name || "Helping Hands"}</span>
            <span className="hidden text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:block">{b.tagline || t("brand_tagline")}</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <Link key={l.id} to={l.to} className="text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid={`nav-link-${l.id}`}>{t(l.key)}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangMenu />
          <ThemeToggle />
          <div className="hidden lg:block"><AuthButtons /></div>
          <Sheet>
            <SheetTrigger className="flex h-9 w-9 items-center justify-center rounded-full border lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-4 w-4" /></SheetTrigger>
            <SheetContent className="flex flex-col gap-4 pt-12">
              {LINKS.map((l) => (
                <Link key={l.id} to={l.to} className="font-serif text-2xl" data-testid={`mobile-nav-link-${l.id}`}>{t(l.key)}</Link>
              ))}
              <div className="mt-4"><AuthButtons mobile /></div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
